import Phaser from "phaser";

export const COLORS = {
  ink: 0x110f0d,
  green: 0x55704a,
  panel: 0x2b241f,
  panelLight: 0x46392f,
  gold: 0xf3c656,
  goldDark: 0xa97928,
  cream: 0xfff3cf,
  red: 0xc64b42,
  blue: 0x4b86c8,
} as const;

export function addCastleBackdrop(scene: Phaser.Scene): void {
  const width = scene.scale.width;
  const height = scene.scale.height;
  scene.cameras.main.setBackgroundColor(COLORS.ink);

  const backdrop = scene.add.graphics().setDepth(-20);
  backdrop.fillGradientStyle(0x19130f, 0x19130f, 0x080706, 0x080706, 1);
  backdrop.fillRect(0, 0, width, height);

  // Distant keep silhouette.
  backdrop.fillStyle(0x090807, 0.88);
  backdrop.fillRect(0, height * 0.62, width, height * 0.38);
  const keepWidth = Math.min(620, width * 0.78);
  const left = (width - keepWidth) / 2;
  backdrop.fillRect(left, height * 0.24, keepWidth, height * 0.5);
  for (let i = 0; i < 7; i += 1) {
    const x = left + (i * keepWidth) / 6;
    backdrop.fillRect(x - 28, height * 0.18, 56, height * 0.16);
    backdrop.fillTriangle(x - 35, height * 0.18, x, height * 0.1, x + 35, height * 0.18);
  }

  // Stonework and warm window light.
  backdrop.lineStyle(1, 0x6f5b49, 0.13);
  for (let y = height * 0.25; y < height * 0.73; y += 24) {
    const offset = Math.floor(y / 24) % 2 === 0 ? 0 : 18;
    for (let x = left + offset; x < left + keepWidth; x += 36) backdrop.strokeRect(x, y, 36, 24);
  }
  for (let i = 0; i < 10; i += 1) {
    const x = left + 58 + ((i * 97) % Math.max(100, keepWidth - 110));
    const y = height * 0.33 + ((i * 61) % Math.max(80, height * 0.3));
    backdrop.fillStyle(0xf4bd51, 0.16).fillRoundedRect(x, y, 9, 18, 4);
  }

  // Vignette and framing.
  backdrop
    .fillStyle(0x000000, 0.28)
    .fillRect(0, 0, width, 48)
    .fillRect(0, height - 48, width, 48);
  backdrop.lineStyle(3, COLORS.goldDark, 0.6).strokeRect(12, 12, width - 24, height - 24);
  backdrop.lineStyle(1, COLORS.gold, 0.25).strokeRect(18, 18, width - 36, height - 36);

  for (let i = 0; i < 18; i += 1) {
    const mote = scene.add
      .circle(
        Phaser.Math.Between(24, Math.max(25, width - 24)),
        Phaser.Math.Between(30, Math.max(31, height - 30)),
        Phaser.Math.FloatBetween(0.8, 2.2),
        0xf1cb77,
        Phaser.Math.FloatBetween(0.12, 0.34),
      )
      .setDepth(-10);
    scene.tweens.add({
      targets: mote,
      y: mote.y - Phaser.Math.Between(25, 85),
      x: mote.x + Phaser.Math.Between(-20, 20),
      alpha: 0,
      duration: Phaser.Math.Between(2600, 5200),
      repeat: -1,
      delay: Phaser.Math.Between(0, 1800),
    });
  }
}

export function addButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  label: string,
  action: () => void,
  accent = false,
): Phaser.GameObjects.Container {
  const height = 50;
  const shadow = scene.add.rectangle(3, 5, width, height, 0x000000, 0.42);
  const background = scene.add
    .rectangle(0, 0, width, height, accent ? 0xc99a35 : COLORS.panel)
    .setStrokeStyle(2, accent ? 0xffe29a : 0x8c7257)
    .setInteractive({ useHandCursor: true });
  const inner = scene.add
    .rectangle(0, 0, width - 8, height - 8, accent ? 0xe7bb55 : COLORS.panelLight, 0.72)
    .setStrokeStyle(1, accent ? 0x8f651d : 0x17120f, 0.8);
  const leftGem = scene.add.polygon(-width / 2 + 14, 0, [0, -8, 7, 0, 0, 8, -7, 0], COLORS.gold);
  const rightGem = scene.add.polygon(width / 2 - 14, 0, [0, -8, 7, 0, 0, 8, -7, 0], COLORS.gold);
  const text = scene.add
    .text(0, 0, label, {
      fontFamily: "Georgia, serif",
      fontSize: "18px",
      fontStyle: "bold",
      color: accent ? "#271b10" : "#fff3cf",
      stroke: accent ? "#f7dd9b" : "#17120f",
      strokeThickness: 1,
    })
    .setOrigin(0.5);
  const container = scene.add.container(x, y, [shadow, background, inner, leftGem, rightGem, text]);

  background.on("pointerover", () => {
    background.setFillStyle(accent ? 0xe1b34d : 0x5a493a);
    scene.tweens.add({ targets: container, scaleX: 1.025, scaleY: 1.025, duration: 90 });
  });
  background.on("pointerout", () => {
    background.setFillStyle(accent ? 0xc99a35 : COLORS.panel);
    scene.tweens.add({ targets: container, scaleX: 1, scaleY: 1, duration: 90 });
  });
  background.on("pointerdown", () => {
    scene.tweens.add({
      targets: container,
      scaleX: 0.98,
      scaleY: 0.98,
      duration: 55,
      yoyo: true,
      onComplete: action,
    });
  });
  return container;
}

export function title(scene: Phaser.Scene, value: string, subtitle: string): void {
  const center = scene.scale.width / 2;
  const plateWidth = Math.min(760, scene.scale.width - 40);
  const half = plateWidth / 2;
  const compact = scene.scale.width < 760;
  const titleMaxWidth = plateWidth - (compact ? 54 : 84);
  const baseTitleSize = compact ? 29 : scene.scale.width < 1100 ? 36 : 41;
  const estimatedWidth = value.length * baseTitleSize * 0.58;
  const fittedTitleSize = Math.max(
    compact ? 23 : 26,
    Math.floor(baseTitleSize * Math.min(1, titleMaxWidth / estimatedWidth)),
  );
  const plate = scene.add.graphics();
  plate.fillStyle(0x17120f, 0.82).fillRoundedRect(center - half, 24, plateWidth, 156, 18);
  plate
    .lineStyle(2, COLORS.goldDark, 0.95)
    .strokeRoundedRect(center - half, 24, plateWidth, 156, 18);
  plate
    .lineStyle(1, COLORS.gold, 0.26)
    .strokeRoundedRect(center - half + 9, 33, plateWidth - 18, 138, 14);
  plate
    .fillStyle(0x7f1f26, 0.9)
    .fillTriangle(center - half + 10, 35, center - half + 90, 35, center - half + 10, 115);
  plate
    .fillStyle(0x7f1f26, 0.9)
    .fillTriangle(center + half - 10, 35, center + half - 90, 35, center + half - 10, 115);

  scene.add.image(center, 62, "ui-crest").setScale(compact ? 0.82 : 1);
  scene.add
    .text(center, 112, value, {
      fontFamily: "Georgia, serif",
      fontSize: `${fittedTitleSize}px`,
      fontStyle: "bold",
      color: "#fff3cf",
      stroke: "#1a120b",
      strokeThickness: compact ? 3 : 4,
      align: "center",
      wordWrap: { width: titleMaxWidth, useAdvancedWrap: true },
      shadow: { offsetX: 0, offsetY: 3, color: "#000000", blur: 5, fill: true },
    })
    .setOrigin(0.5);
  scene.add
    .text(center, 153, subtitle, {
      fontFamily: "Trebuchet MS, sans-serif",
      fontSize: compact ? "12px" : "15px",
      color: "#e8cf91",
      align: "center",
      wordWrap: { width: plateWidth - 52, useAdvancedWrap: true },
    })
    .setOrigin(0.5);
}

export interface OrnatePanelOptions {
  width: number;
  height: number;
  accent?: boolean;
  interactive?: boolean;
  selected?: boolean;
  disabled?: boolean;
}

export function addOrnatePanel(
  scene: Phaser.Scene,
  x: number,
  y: number,
  options: OrnatePanelOptions,
): Phaser.GameObjects.Container {
  const { width, height } = options;
  const accent = options.accent ?? false;
  const selected = options.selected ?? false;
  const disabled = options.disabled ?? false;
  const frameColor = selected ? COLORS.gold : accent ? 0x9f7b3d : 0x745f4a;
  const panelColor = disabled ? 0x24211f : COLORS.panel;
  const innerColor = disabled ? 0x302c29 : COLORS.panelLight;

  const shadow = scene.add.rectangle(5, 7, width, height, 0x000000, 0.38);
  const frame = scene.add
    .rectangle(0, 0, width, height, panelColor, 0.98)
    .setStrokeStyle(selected ? 4 : 3, frameColor, 1);
  const inner = scene.add
    .rectangle(0, 0, width - 14, height - 14, innerColor, 0.82)
    .setStrokeStyle(1, selected ? 0xffe39a : 0x1a1511, 0.85);

  const topRule = scene.add.rectangle(0, -height / 2 + 18, width - 34, 2, frameColor, 0.72);
  const bottomRule = scene.add.rectangle(0, height / 2 - 18, width - 34, 2, frameColor, 0.42);
  const cornerSize = 10;
  const corners = [
    scene.add.polygon(
      -width / 2 + 14,
      -height / 2 + 14,
      [0, -cornerSize, cornerSize, 0, 0, cornerSize, -cornerSize, 0],
      frameColor,
    ),
    scene.add.polygon(
      width / 2 - 14,
      -height / 2 + 14,
      [0, -cornerSize, cornerSize, 0, 0, cornerSize, -cornerSize, 0],
      frameColor,
    ),
    scene.add.polygon(
      -width / 2 + 14,
      height / 2 - 14,
      [0, -cornerSize, cornerSize, 0, 0, cornerSize, -cornerSize, 0],
      frameColor,
    ),
    scene.add.polygon(
      width / 2 - 14,
      height / 2 - 14,
      [0, -cornerSize, cornerSize, 0, 0, cornerSize, -cornerSize, 0],
      frameColor,
    ),
  ];

  const container = scene.add.container(x, y, [
    shadow,
    frame,
    inner,
    topRule,
    bottomRule,
    ...corners,
  ]);
  container.setAlpha(disabled ? 0.58 : 1);

  if (options.interactive && !disabled) {
    frame.setInteractive({ useHandCursor: true });
    frame.on("pointerover", () => {
      frame.setFillStyle(0x3a3028);
      scene.tweens.add({
        targets: container,
        y: y - 4,
        scaleX: 1.015,
        scaleY: 1.015,
        duration: 100,
      });
    });
    frame.on("pointerout", () => {
      frame.setFillStyle(panelColor);
      scene.tweens.add({ targets: container, y, scaleX: 1, scaleY: 1, duration: 100 });
    });
  }

  container.setData("hit-area", frame);
  return container;
}

export function addStatusBadge(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  tone: "gold" | "green" | "red" | "muted" = "muted",
): Phaser.GameObjects.Container {
  const palette = {
    gold: { fill: 0x8f6825, stroke: 0xf1cb69, text: "#fff0b5" },
    green: { fill: 0x315b42, stroke: 0x79a77f, text: "#d9f0d9" },
    red: { fill: 0x6f2f2c, stroke: 0xc96b60, text: "#ffd3cb" },
    muted: { fill: 0x3c342d, stroke: 0x766452, text: "#d9ccb2" },
  } as const;
  const colors = palette[tone];
  const text = scene.add
    .text(0, 0, label, {
      fontFamily: "Trebuchet MS, sans-serif",
      fontSize: "11px",
      fontStyle: "bold",
      color: colors.text,
    })
    .setOrigin(0.5);
  const badgeWidth = Math.max(76, text.width + 24);
  const background = scene.add
    .rectangle(0, 0, badgeWidth, 26, colors.fill, 0.96)
    .setStrokeStyle(1, colors.stroke, 0.9);
  return scene.add.container(x, y, [background, text]);
}

export function addLittleCastleFooter(scene: Phaser.Scene, label = "LittleCastle Games"): void {
  const text = scene.add
    .text(scene.scale.width - 18, scene.scale.height - 14, `♜  ${label}`, {
      fontFamily: "Georgia, serif",
      fontSize: "12px",
      color: "#d8c58e",
    })
    .setOrigin(1, 1)
    .setDepth(200)
    .setAlpha(0.72);
  text.setShadow(0, 1, "#000000", 2, true, true);
}

export function addRoyalCelebration(scene: Phaser.Scene, victory: boolean): void {
  const centerX = scene.scale.width / 2;
  const centerY = scene.scale.height / 2;
  const color = victory ? COLORS.gold : COLORS.red;
  for (let index = 0; index < 34; index += 1) {
    const particle = scene.add.rectangle(centerX, centerY, 5, 10, color, 0.9).setDepth(80);
    const angle = Phaser.Math.FloatBetween(-Math.PI, Math.PI);
    const distance = Phaser.Math.Between(90, 330);
    scene.tweens.add({
      targets: particle,
      x: centerX + Math.cos(angle) * distance,
      y: centerY + Math.sin(angle) * distance + (victory ? 60 : 120),
      angle: Phaser.Math.Between(-220, 220),
      alpha: 0,
      duration: Phaser.Math.Between(900, 1700),
      ease: "Cubic.Out",
      onComplete: () => particle.destroy(),
    });
  }
}
